import { useEffect, useState, useCallback } from 'react'
import { useTerrariumStore } from '../store/terrariumStore'
import useSettingsStore from '../store/settingsStore'
import TerrariumPlantSlot from './TerrariumPlantSlot'
import TerrariumInsect from './TerrariumInsect'
import BitsEffects from './BitsEffects'
import '../styles/WideTerrariumScene.css'

export function WideTerrariumScene() {
  const plants = useTerrariumStore((state) => state.plants)
  const insects = useTerrariumStore((state) => state.insects)
  const lastAction = useTerrariumStore((state) => state.lastAction)
  const settings = useSettingsStore((state) => state.settings)
  const [notifications, setNotifications] = useState([])

  // Notification helper
  const addNotification = useCallback((icon, text, type = 'info') => {
    const id = Date.now() + Math.random()
    setNotifications(prev => [...prev, { id, icon, text, type }])
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id))
    }, 4000)
  }, [])

  // Show notifications for actions
  useEffect(() => {
    if (!lastAction) return
    const { action, username, targetUsername, plantType, insectType } = lastAction

    if (action === 'planted') {
      addNotification('🌱', `${username} planted a ${plantType || 'seed'}!`, 'plant')
    } else if (action === 'watered') {
      const target = targetUsername ? `${targetUsername}'s plant` : 'the plant'
      addNotification('💧', `${username} watered ${target}!`, 'water')
    } else if (action === 'joined') {
      addNotification('🐛', `${username} joined as a ${insectType}!`, 'join')
    } else if (action === 'pollinated') {
      addNotification('🌸', `${username} pollinated ${targetUsername}'s plant!`, 'pollinate')
    }
  }, [lastAction, addNotification])

  const plantEntries = Object.entries(plants)
  const insectEntries = Object.entries(insects)
  const isEmpty = plantEntries.length === 0 && insectEntries.length === 0

  return (
    <div className="wide-terrarium-container no-background">
      {/* Event Notifications */}
      {settings.showNotifications && (
        <div className="terrarium-notifications">
          {notifications.map((notification, index) => (
            <div
              key={notification.id}
              className={`terrarium-notification notification-${notification.type}`}
              style={{ '--index': index }}
            >
              <span className="notification-icon">{notification.icon}</span>
              <span className="notification-text">{notification.text}</span>
            </div>
          ))}
        </div>
      )}

      <div className="wide-terrarium-glass">
        {/* Glass rim */}
        {settings.showGlassFrame && <div className="glass-rim" />}

        {/* Ground layers */}
        {settings.showGround && (
          <div className="terrarium-ground">
            <div className="ground-moss" />
            <div className="ground-soil" />
          </div>
        )}

        {/* Plant slots */}
        <div className="terrarium-entities-layer">
          {plantEntries.map(([userId, plant]) => (
            <TerrariumPlantSlot key={userId} plant={plant} />
          ))}
        </div>

        {/* Insects layer */}
        <div className="terrarium-insects-layer">
          {insectEntries.map(([userId, insect]) => (
            <TerrariumInsect key={userId} insect={insect} />
          ))}
        </div>

        {/* Glass shine overlay */}
        {settings.showGlassFrame && <div className="glass-shine" />}
      </div>

      {/* Bits Effects Layer */}
      <BitsEffects />

      {/* Status bar */}
      {settings.showGrowthBars && plantEntries.length > 0 && (
        <div className="terrarium-status-bar">
          <span className="status-count">
            🌱 {plantEntries.length} plant{plantEntries.length !== 1 ? 's' : ''}
          </span>
          <span className="status-count">
            🐛 {insectEntries.length} insect{insectEntries.length !== 1 ? 's' : ''}
          </span>
        </div>
      )}
    </div>
  )
}

export default WideTerrariumScene